import React from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_screen-studio-3/artifacts/rr2m67nx_logo%20valnexo%20vectorise%CC%81.png";

/**
 * Virtual Studio Background Component
 * Creates a realistic-looking studio environment with Valnexo branding
 */
export const ValnexoStudioBackground = ({ width = 1280, height = 720 }) => {
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 1280 720" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      <defs>
        {/* Gradients for realistic lighting */}
        <linearGradient id="wallGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a1f2e" />
          <stop offset="50%" stopColor="#0f1219" />
          <stop offset="100%" stopColor="#0a0d12" />
        </linearGradient>
        
        <linearGradient id="floorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#0d0d0d" />
        </linearGradient>
        
        <linearGradient id="accentLight" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#1e40af" stopOpacity="0" />
        </linearGradient>
        
        <linearGradient id="boardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f1f5f9" />
        </linearGradient>
        
        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="10" stdDeviation="20" floodColor="#000" floodOpacity="0.5"/>
        </filter>
        
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        {/* Wood texture pattern */}
        <pattern id="woodPattern" patternUnits="userSpaceOnUse" width="100" height="100">
          <rect width="100" height="100" fill="#2a1810"/>
          <line x1="0" y1="20" x2="100" y2="20" stroke="#3d2317" strokeWidth="2" opacity="0.5"/>
          <line x1="0" y1="50" x2="100" y2="50" stroke="#3d2317" strokeWidth="1" opacity="0.3"/>
          <line x1="0" y1="80" x2="100" y2="80" stroke="#3d2317" strokeWidth="2" opacity="0.5"/>
        </pattern>
      </defs>
      
      {/* Background wall */}
      <rect width="1280" height="720" fill="url(#wallGradient)" />
      
      {/* Ambient lighting effect from top */}
      <ellipse cx="640" cy="0" rx="600" ry="300" fill="url(#accentLight)" />
      
      {/* Floor with perspective */}
      <path d="M 0 550 L 1280 550 L 1280 720 L 0 720 Z" fill="url(#floorGradient)" />
      
      {/* Floor reflection line */}
      <line x1="0" y1="550" x2="1280" y2="550" stroke="#2563eb" strokeWidth="2" opacity="0.3" />
      
      {/* Left wall panel (perspective) */}
      <path d="M 0 0 L 150 50 L 150 500 L 0 550 Z" fill="#0d1117" opacity="0.8" />
      
      {/* Right wall panel (perspective) */}
      <path d="M 1280 0 L 1130 50 L 1130 500 L 1280 550 Z" fill="#0d1117" opacity="0.8" />
      
      {/* Decorative LED strip on ceiling */}
      <rect x="200" y="30" width="880" height="4" fill="#2563eb" opacity="0.6" filter="url(#glow)" rx="2" />
      
      {/* Main whiteboard/tableau */}
      <g filter="url(#softShadow)">
        {/* Board frame */}
        <rect x="340" y="100" width="600" height="380" fill="#1e293b" rx="8" />
        {/* Board surface */}
        <rect x="350" y="110" width="580" height="360" fill="url(#boardGradient)" rx="4" />
        
        {/* Board subtle grid lines */}
        <g opacity="0.1">
          <line x1="350" y1="170" x2="930" y2="170" stroke="#94a3b8" strokeWidth="1" />
          <line x1="350" y1="230" x2="930" y2="230" stroke="#94a3b8" strokeWidth="1" />
          <line x1="350" y1="290" x2="930" y2="290" stroke="#94a3b8" strokeWidth="1" />
          <line x1="350" y1="350" x2="930" y2="350" stroke="#94a3b8" strokeWidth="1" />
          <line x1="350" y1="410" x2="930" y2="410" stroke="#94a3b8" strokeWidth="1" />
        </g>
      </g>
      
      {/* Valnexo Logo placeholder on board - centered */}
      <g transform="translate(540, 200)">
        {/* Logo background circle */}
        <circle cx="100" cy="80" r="70" fill="#f8fafc" />
        {/* Blue accent behind logo */}
        <circle cx="100" cy="80" r="60" fill="#2563eb" opacity="0.1" />
      </g>
      
      {/* "VALNEXO ACADEMY STUDIO" text below logo */}
      <text 
        x="640" 
        y="370" 
        textAnchor="middle" 
        fontFamily="'Outfit', sans-serif" 
        fontSize="28" 
        fontWeight="600" 
        fill="#1e293b"
        letterSpacing="2"
      >
        VALNEXO ACADEMY
      </text>
      <text 
        x="640" 
        y="405" 
        textAnchor="middle" 
        fontFamily="'Outfit', sans-serif" 
        fontSize="18" 
        fontWeight="400" 
        fill="#64748b"
        letterSpacing="4"
      >
        STUDIO
      </text>
      
      {/* Decorative plant on left */}
      <g transform="translate(80, 380)">
        {/* Pot */}
        <path d="M 0 120 L 10 170 L 70 170 L 80 120 Z" fill="#374151" />
        <ellipse cx="40" cy="120" rx="45" ry="15" fill="#4b5563" />
        {/* Plant leaves */}
        <path d="M 40 120 Q 20 80 35 40" stroke="#22c55e" strokeWidth="3" fill="none" />
        <path d="M 40 120 Q 60 70 50 30" stroke="#22c55e" strokeWidth="3" fill="none" />
        <path d="M 40 120 Q 30 60 45 20" stroke="#16a34a" strokeWidth="4" fill="none" />
        <ellipse cx="35" cy="40" rx="15" ry="25" fill="#22c55e" transform="rotate(-20, 35, 40)" />
        <ellipse cx="50" cy="30" rx="12" ry="22" fill="#16a34a" transform="rotate(10, 50, 30)" />
        <ellipse cx="45" cy="20" rx="14" ry="28" fill="#15803d" transform="rotate(-5, 45, 20)" />
      </g>
      
      {/* Decorative plant on right */}
      <g transform="translate(1100, 400)">
        {/* Pot */}
        <path d="M 0 100 L 8 140 L 72 140 L 80 100 Z" fill="#374151" />
        <ellipse cx="40" cy="100" rx="42" ry="12" fill="#4b5563" />
        {/* Plant */}
        <path d="M 40 100 Q 25 60 40 20" stroke="#22c55e" strokeWidth="3" fill="none" />
        <path d="M 40 100 Q 55 55 45 15" stroke="#16a34a" strokeWidth="3" fill="none" />
        <ellipse cx="40" cy="25" rx="20" ry="30" fill="#22c55e" transform="rotate(-10, 40, 25)" />
        <ellipse cx="45" cy="15" rx="18" ry="28" fill="#15803d" transform="rotate(5, 45, 15)" />
      </g>
      
      {/* Accent lights on sides */}
      <rect x="180" y="150" width="4" height="200" fill="#2563eb" opacity="0.4" filter="url(#glow)" rx="2" />
      <rect x="1096" y="150" width="4" height="200" fill="#2563eb" opacity="0.4" filter="url(#glow)" rx="2" />
      
      {/* Subtle desk/table at bottom */}
      <g>
        <path d="M 300 520 L 980 520 L 1000 550 L 280 550 Z" fill="#1e293b" />
        <rect x="300" y="510" width="680" height="12" fill="#334155" rx="2" />
        {/* Desk LED strip */}
        <rect x="310" y="518" width="660" height="2" fill="#2563eb" opacity="0.5" />
      </g>
      
      {/* Floating shelf on right with books */}
      <g transform="translate(1000, 200)">
        <rect x="0" y="0" width="100" height="8" fill="#374151" />
        {/* Books */}
        <rect x="10" y="-60" width="15" height="60" fill="#ef4444" rx="1" />
        <rect x="28" y="-50" width="12" height="50" fill="#3b82f6" rx="1" />
        <rect x="43" y="-55" width="14" height="55" fill="#22c55e" rx="1" />
        <rect x="60" y="-45" width="10" height="45" fill="#f59e0b" rx="1" />
      </g>
      
      {/* Bottom vignette effect */}
      <rect 
        x="0" y="600" width="1280" height="120" 
        fill="url(#floorGradient)" 
        opacity="0.8"
      />
    </svg>
  );
};

/**
 * Generates a studio background as a data URL for use in canvas
 */
export const generateStudioBackgroundDataUrl = (width = 1280, height = 720) => {
  const svgString = `
    <svg width="${width}" height="${height}" viewBox="0 0 1280 720" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="wallGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#1a1f2e" />
          <stop offset="50%" stop-color="#0f1219" />
          <stop offset="100%" stop-color="#0a0d12" />
        </linearGradient>
        <linearGradient id="floorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#1a1a1a" />
          <stop offset="100%" stop-color="#0d0d0d" />
        </linearGradient>
        <linearGradient id="accentLight" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#2563eb" stop-opacity="0.3" />
          <stop offset="100%" stop-color="#1e40af" stop-opacity="0" />
        </linearGradient>
        <linearGradient id="boardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#ffffff" />
          <stop offset="100%" stop-color="#f1f5f9" />
        </linearGradient>
      </defs>
      
      <!-- Background -->
      <rect width="1280" height="720" fill="url(#wallGradient)" />
      <ellipse cx="640" cy="0" rx="600" ry="300" fill="url(#accentLight)" />
      
      <!-- Floor -->
      <path d="M 0 550 L 1280 550 L 1280 720 L 0 720 Z" fill="url(#floorGradient)" />
      <line x1="0" y1="550" x2="1280" y2="550" stroke="#2563eb" stroke-width="2" opacity="0.3" />
      
      <!-- Side walls -->
      <path d="M 0 0 L 150 50 L 150 500 L 0 550 Z" fill="#0d1117" opacity="0.8" />
      <path d="M 1280 0 L 1130 50 L 1130 500 L 1280 550 Z" fill="#0d1117" opacity="0.8" />
      
      <!-- LED strip -->
      <rect x="200" y="30" width="880" height="4" fill="#2563eb" opacity="0.6" rx="2" />
      
      <!-- Whiteboard -->
      <rect x="340" y="100" width="600" height="380" fill="#1e293b" rx="8" />
      <rect x="350" y="110" width="580" height="360" fill="url(#boardGradient)" rx="4" />
      
      <!-- Grid lines -->
      <g opacity="0.1">
        <line x1="350" y1="170" x2="930" y2="170" stroke="#94a3b8" stroke-width="1" />
        <line x1="350" y1="230" x2="930" y2="230" stroke="#94a3b8" stroke-width="1" />
        <line x1="350" y1="290" x2="930" y2="290" stroke="#94a3b8" stroke-width="1" />
        <line x1="350" y1="350" x2="930" y2="350" stroke="#94a3b8" stroke-width="1" />
        <line x1="350" y1="410" x2="930" y2="410" stroke="#94a3b8" stroke-width="1" />
      </g>
      
      <!-- Logo area -->
      <circle cx="640" cy="230" r="50" fill="#f8fafc" />
      <circle cx="640" cy="230" r="42" fill="#2563eb" opacity="0.15" />
      
      <!-- Text -->
      <text x="640" y="320" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" font-weight="bold" fill="#1e293b" letter-spacing="2">VALNEXO ACADEMY</text>
      <text x="640" y="350" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" fill="#64748b" letter-spacing="4">STUDIO</text>
      
      <!-- Accent lights -->
      <rect x="180" y="150" width="4" height="200" fill="#2563eb" opacity="0.4" rx="2" />
      <rect x="1096" y="150" width="4" height="200" fill="#2563eb" opacity="0.4" rx="2" />
      
      <!-- Desk -->
      <path d="M 300 520 L 980 520 L 1000 550 L 280 550 Z" fill="#1e293b" />
      <rect x="300" y="510" width="680" height="12" fill="#334155" rx="2" />
      <rect x="310" y="518" width="660" height="2" fill="#2563eb" opacity="0.5" />
      
      <!-- Plants (simplified) -->
      <ellipse cx="120" cy="480" rx="30" ry="50" fill="#22c55e" />
      <ellipse cx="115" cy="460" rx="25" ry="45" fill="#16a34a" />
      <rect x="100" y="520" width="40" height="30" fill="#374151" />
      
      <ellipse cx="1140" cy="490" rx="25" ry="45" fill="#22c55e" />
      <ellipse cx="1145" cy="475" rx="22" ry="40" fill="#16a34a" />
      <rect x="1120" y="530" width="40" height="25" fill="#374151" />
      
      <!-- Bookshelf -->
      <rect x="1020" y="220" width="80" height="6" fill="#374151" />
      <rect x="1025" y="175" width="12" height="45" fill="#ef4444" />
      <rect x="1040" y="180" width="10" height="40" fill="#3b82f6" />
      <rect x="1053" y="172" width="11" height="48" fill="#22c55e" />
      <rect x="1067" y="185" width="9" height="35" fill="#f59e0b" />
    </svg>
  `;
  
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
};

/**
 * Studio background presets
 */
export const STUDIO_BACKGROUNDS = {
  valnexoStudio: {
    id: 'valnexo-studio',
    name: 'Studio Valnexo',
    description: 'Studio professionnel avec tableau et logo',
    type: 'generated',
    generator: generateStudioBackgroundDataUrl
  },
  blur: {
    id: 'blur',
    name: 'Flou',
    description: 'Arrière-plan flou',
    type: 'blur'
  },
  darkBlue: {
    id: 'dark-blue',
    name: 'Bleu foncé',
    description: 'Couleur unie professionnelle',
    type: 'color',
    color: '#0F172A'
  },
  darkGray: {
    id: 'dark-gray',
    name: 'Gris foncé',
    description: 'Couleur neutre',
    type: 'color',
    color: '#1F2937'
  },
  gradient: {
    id: 'gradient',
    name: 'Dégradé bleu',
    description: 'Dégradé moderne',
    type: 'gradient',
    colors: ['#1e3a5f', '#0f172a']
  }
};

export default ValnexoStudioBackground;
